import {AChartX} from "@atiautomacao/ati-ui-library";
import React, {FunctionComponent, useEffect, useState} from "react";
import {useAppSelector} from "../../../../Config/Hooks";
import {useTheme} from "@mui/material";
import {formatNumberWithMaxTwoDecimals} from "../../../../Utils/NumberUtil";
import DataNotFound from "../../../../Shared/Components/DataNotFoundMessage";


const UnavailabilityBarChart: FunctionComponent<{data: any, isExpansive?: boolean, height?:number}> = ({data, isExpansive = false, height = 400}) => {
    const openSubMenu = useAppSelector((state: any) => state.layout.openSubMenu);
    const theme = useTheme();
    const processedData = React.useMemo(() => {
        if (!data || data.length === 0) return [];
        const groupedMap = new Map<number, any>();

        data.forEach((item: any) => {
            if (!groupedMap.has(item.powerStationId)) {
                groupedMap.set(item.powerStationId, {
                    id: item.powerStationId,
                    name: item.powerStationName,
                    din: 0,
                    dex: 0,
                    total: 0
                });
            }

            const station = groupedMap.get(item.powerStationId);

            item.unavailabilityItem.forEach((uType: any) => {
                const formattedValue = formatNumberWithMaxTwoDecimals(uType.value);

                if (uType.name === 'DIN') {
                    station.din += formattedValue;
                } else if (uType.name === 'DEX') {
                    station.dex += formattedValue;
                }
            });
            station.total = station.din + station.dex;
        });

        const allStations = Array.from(groupedMap.values());
        allStations.sort((a, b) => b.total - a.total);

        if (allStations.length <= (isExpansive ? 30 : 20)) return allStations;

        const limit = isExpansive ? 29 : 19;
        const topItems = allStations.slice(0, limit);
        const remaining = allStations.slice(limit);

        const others = {
            id: 'others',
            name: 'Outras',
            din: remaining.reduce((sum, s) => sum + s.din, 0),
            dex: remaining.reduce((sum, s) => sum + s.dex, 0),
            total: remaining.reduce((sum, s) => sum + s.total, 0),
        };

        return [...topItems, others];
    }, [data, isExpansive]);

    // @ts-ignore
    const option: AChartXProps['option'] = React.useMemo(() => ({
        color: ['#2d5de7','#03a9f4'],
        toolbox: {show: false },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' }
        },
        legend: {
            data: ['DIN (Disponibilidade Interna)', 'DEX (Disponibilidade Externa)'],
            bottom: 0,
        },
        grid: {
            containLabel: true,
            top: 10,
            bottom: 30,
            right: 50,
            left: 40,
        },
        dataset: {
            source: processedData
        },
        xAxis: {
            type: 'category',
            data: processedData.map(item => item.name),
            axisLabel: {
                interval: 0,
                rotate: 40,
                formatter: function(value:any) {
                    return value.length > 10 ? value.substring(0, 10) + '...' : value;
                }}
        },
        yAxis: {
            type: 'value',
            name: 'kWh',
            nameRotate: 90,
            nameLocation: 'middle',
            nameGap: 40,
            splitLine: {
                lineStyle: {
                    type: 'dashed',
                    color: '#e0e0e0'
                }
            },
            axisLine: { show: false },
            axisTick: { show: false }
        },
        series: [
            {
                name: 'DIN (Disponibilidade Interna)',
                type: 'bar',
                stack: 'total',
                data: processedData.map(item => ({
                    value: item.din,
                    itemStyle: { color: item.name === 'Outras' ? '#757575' : '#2d5de7' }
                }))
            },
            {
                name: 'DEX (Disponibilidade Externa)',
                type: 'bar',
                stack: 'total',
                data: processedData.map(item => ({
                    value: item.dex,
                    itemStyle: { color: item.name === 'Outras' ? '#bdbdbd' : '#03a9f4' }
                })),
                itemStyle: {
                    borderRadius: [4, 4, 0, 0]
                }
            },

        ]
    }),[processedData, theme.palette.mode]);

    const [content, setContent] = useState<JSX.Element>();
    useEffect(() => {
        if(data && data.length > 0){
            setContent(
                <AChartX
                key={`din-dex-chart-${openSubMenu}`}
                option={option}
                width={'100%'}
                height={height}
                loading={false}
                theme={theme.palette.mode}
            />)
        }else{
            setContent(<DataNotFound boxStyle={{ height: height, width: '100%' }} />)
        }
    }, [data, openSubMenu]);

    return (
        <>
            {content}
        </>

    );
}

export default UnavailabilityBarChart;